import React, { useState, useEffect } from "react";
import { Button, Form, Checkbox, Message } from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import DataTable, { FilterProps, DataType } from "../../app/common/DataTable";
import { Member } from "../../app/models/member";
import { useTranslation } from "react-i18next";
import { useMemberStatusOptions } from "../../app/common/enums/memberStatus";
import ConfirmComponent from "../../app/common/form/ConfirmComponent";

export default observer(function SendEmailPage() {
    const { memberStore, emailTemplateStore, emailLogStore, modalStore } = useStore();
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
    const [templateId, setTemplateId] = useState<string>("");
    const [customBody, setCustomBody] = useState("");
    const [useTemplate, setUseTemplate] = useState(true);
    const { t } = useTranslation();
    const memberStatusOptions = useMemberStatusOptions();

    useEffect(() => {
        emailTemplateStore.fetchTemplates();
    }, [emailTemplateStore]);

    function getFilterProps(): FilterProps {
        const filters: any = {};
        return { filters, page: 1, pageSize: 10 };
    }

    const handleSend = async () => {
        await emailLogStore.sendEmail({
            memberIds: selectedMemberIds,
            templateId: useTemplate ? templateId : undefined,
            body: useTemplate ? undefined : customBody,
        });
    };

    const handleSendConfirm = () => {
        modalStore.openModal(
            <ConfirmComponent
                text={t("general.areYouSure")}
                onYes={async () => {
                    handleSend();
                    modalStore.closeModal();
                }}
                onCancel={modalStore.closeModal}
            />
        );
    };

    const handleGenerateBatchPdf = async () => {
        await emailLogStore.generateBatchLettersPdf(selectedMemberIds, useTemplate ? templateId : undefined, useTemplate ? undefined : customBody);
    };

    const columns = [
        {
            key: "memberNumber", header: t("members.memberNumber"), dataType: DataType.NUMBER, filterable: true,
        },
        {
            key: "firstName", header: t("members.firstName"), filterable: true,
        },
        {
            key: "lastName", header: t("members.lastName"), filterable: true,
        },
        {
            key: "email", header: t("members.email"), filterable: true,
        },
        {
            key: "status",
            header: t("members.status"),
            options: memberStatusOptions,
            dataType: DataType.SELECT,
            filterable: true,
            width: '80px'
        },
    ];

    const handleRowSelect = (member: Member, checked: boolean) => {
        setSelectedMemberIds((prev) =>
            checked
                ? [...prev, member.id!]
                : prev.filter((id) => id !== member.id)
        );
    };

    const handleSelectAll = async () => {
        const currentOrderBy = memberStore.tableData.orderBy || "memberNumber";
        const currentSortDirection = memberStore.tableData.orderBy || "asc";
        const filterProps = { ...getFilterProps(), page: 1, pageSize: 10000, orderBy: currentOrderBy, sortDirection: currentSortDirection };
        const allMembersResult = await memberStore.loadMembers(filterProps);
        if (allMembersResult && allMembersResult.data) {
            setSelectedMemberIds(allMembersResult.data.map((m: Member) => m.id!));
            memberStore.loadMembers({ ...getFilterProps(), orderBy: currentOrderBy, sortDirection: currentSortDirection });
        } else {
            const allIds = Array.from(memberStore.tableData.list.values()).map((m) => m.id!);
            setSelectedMemberIds(allIds);
        }
    };

    const handleDeselectAll = () => {
        setSelectedMemberIds([]);
        const currentOrderBy = memberStore.tableData.orderBy || "memberNumber";
        const currentSortDirection = memberStore.tableData.orderBy || "asc";
        memberStore.loadMembers({ ...getFilterProps(), orderBy: currentOrderBy, sortDirection: currentSortDirection });
    };

    return (
        <Form>
            <h2>{t("emails.send.title")}</h2>
            <Button type="button" onClick={handleSelectAll} size="small" basic>
                {t("emails.send.selectAll")}
            </Button>
            <Button type="button" onClick={handleDeselectAll} size="small" basic>
                {t("emails.send.deselectAll")}
            </Button>

            <DataTable
                columns={[
                    {
                        key: "select",
                        header: "",
                        render: (item: Member) => (
                            <Checkbox
                                checked={selectedMemberIds.includes(item.id!)}
                                onChange={(_, data) => handleRowSelect(item, !!data.checked)}
                            />
                        ),
                        width: "50px",
                    },
                    ...columns,
                ]}
                data={memberStore.tableData}
                onFilter={(filterProps: FilterProps) => memberStore.loadMembers(filterProps)}
                disableRowClick={true}
                defaultOrderByColumn="memberNumber"
            />

            <Form.Field>
                <Checkbox
                    label={t("emails.send.useTemplate")}
                    checked={useTemplate}
                    onChange={(_, data) => setUseTemplate(!!data.checked)}
                    style={{ marginTop: "20px" }}
                />
            </Form.Field>
            {useTemplate ? (
                <Form.Select
                    label={t("emails.send.template")}
                    options={emailTemplateStore.templates.map(tpl => ({
                        key: tpl.id,
                        value: tpl.id,
                        text: tpl.subject
                    }))}
                    value={templateId}
                    onChange={(_, data) => setTemplateId(data.value as string)}
                    placeholder={t("emails.send.template")}
                />
            ) : (
                <>
                    <Form.TextArea
                        label={t("emails.send.customBody")}
                        value={customBody}
                        onChange={e => setCustomBody(e.target.value)}
                    />
                    <Message info style={{ marginBottom: 24 }}>
                        <Message.Header>{t("emails.templates.variablesTitle")}</Message.Header>
                        <p>{t("emails.templates.variablesDescription")}</p>
                        <Message.List>
                            <Message.Item>{t("emails.templates.variableMemberNumber")}</Message.Item>
                            <Message.Item>{t("emails.templates.variableFirstName")}</Message.Item>
                            <Message.Item>{t("emails.templates.variableLastName")}</Message.Item>
                        </Message.List>
                    </Message>
                </>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", paddingBottom: 24, marginTop: 24, marginBottom: 32 }}>
                <Button
                    onClick={handleSendConfirm}
                    positive
                    disabled={selectedMemberIds.length === 0}
                >
                    {t("emails.send.send")}
                </Button>
                <Button
                    onClick={handleGenerateBatchPdf}
                    color="blue"
                    disabled={selectedMemberIds.length === 0}
                >
                    {t("emails.send.generateBatchPdf")}
                </Button>
            </div>
        </Form>
    );
});

